import ButtonBase from '@/global/components/Button/ButtonBase'
import Menu from '@/global/components/Menu'
import { cn } from '@/lib/utils'
import localeText from '@/locales/datagrid'
import { flexRender, Header } from '@tanstack/react-table'
import {
    ArrowDownIcon,
    ArrowUpDownIcon,
    ArrowUpIcon,
    EllipsisVerticalIcon,
    HelpCircleIcon,
} from 'lucide-react'
import { ColumnDefPro } from '../../utils/types'

interface HeaderProp {
    header: Header<any, any>
}

const BUTTON_CLASSES = [
    'size-6 p-1 max-h-full [&_svg]:size-5',
    'text-foreground border-none rounded-full',
    'bg-transparent opacity-0 hover:bg-card group-hover:opacity-100',
    'transition-[opacity_colors] duration-150',
] as const

function SortingButton({ header }: HeaderProp) {
    if (header.column.columnDef.enableSorting === false) return null

    const sorting = header.column.getIsSorted(),
        Icon = !sorting ? (
            <ArrowUpDownIcon
                className={cn({
                    'opacity-90': !sorting,
                })}
            />
        ) : sorting === 'asc' ? (
            <ArrowUpIcon
                className={cn({
                    'opacity-90': !sorting,
                })}
            />
        ) : (
            <ArrowDownIcon
                className={cn({
                    'opacity-90': !sorting,
                })}
            />
        ),
        sortingIndex = sorting === false ? null : header.column.getSortIndex()

    return (
        <ButtonBase
            title={localeText.columnHeaderSortIconLabel}
            variant="ghost"
            size="icon"
            onClick={() => {
                switch (sorting) {
                    case 'asc':
                        header.column.toggleSorting(true, true)
                        break
                    case 'desc':
                        header.column.clearSorting()
                        break
                    default:
                        header.column.toggleSorting(false, true)
                }
            }}
            className={cn(...BUTTON_CLASSES, {
                'bg-card opacity-100 relative': sorting,
            })}
        >
            {Icon}
            {sortingIndex !== null && (
                <sup className="text-xs font-medium tabular-nums ml-0.5 absolute top-0 -right-[1px] ring-card bg-card ring-2 rounded-full">
                    {sortingIndex + 1}
                </sup>
            )}
        </ButtonBase>
    )
}

function ColumnMenu({ header }: HeaderProp) {
    if ((header.column.columnDef as ColumnDefPro)._ogDef.disableColumnMenu)
        return null

    const menuItems: React.ReactNode[] = []
    if (header.column.columnDef.enableSorting !== false) {
        const sortingState = header.column.getIsSorted()
        menuItems.push(
            <Menu.Item
                key="reset-sort"
                onClick={() => header.column.clearSorting()}
                disabled={sortingState === false}
            >
                {localeText.columnMenuUnsort}
            </Menu.Item>,
            <Menu.Item
                key="sort-asc"
                onClick={() => header.column.toggleSorting(false, true)}
                disabled={sortingState === 'asc'}
            >
                {localeText.columnMenuSortAsc}
            </Menu.Item>,
            <Menu.Item
                key="sort-desc"
                onClick={() => header.column.toggleSorting(true, true)}
                disabled={sortingState === 'desc'}
            >
                {localeText.columnMenuSortDesc}
            </Menu.Item>,
            <Menu.Item
                key="ajuda"
                onClick={() => {
                    window.open(
                        'https://docs.farolsoftware.com.br/d/ponto-agil/Ordenacao-das-colunas',
                        '_blank',
                    )
                }}
            >
                <HelpCircleIcon className="size-5 mr-2" /> Ajuda
            </Menu.Item>,
        )
    }

    if (menuItems.length)
        return (
            <Menu.Root arrow className="inline-flex items-center">
                <Menu.Trigger>
                    <ButtonBase
                        variant="ghost"
                        size="icon"
                        className={cn(
                            ...BUTTON_CLASSES,
                            'aria-expanded:bg-card aria-expanded:opacity-100 rounded-full',
                        )}
                    >
                        <EllipsisVerticalIcon />
                    </ButtonBase>
                </Menu.Trigger>
                <Menu.List>{menuItems}</Menu.List>
            </Menu.Root>
        )
}

export function HeaderRenderer({ header }: { header: HeaderProp['header'] }) {
    if (header.isPlaceholder) return null

    const isParentHeader = header.subHeaders && header.subHeaders.length > 0

    return [
        <Cell key="header-cell" header={header} />,
        <span
            key="header-menu"
            className={cn(
                'absolute right-0 top-0 bottom-0 flex items-center mr-1',
                'bg-transparent group-hover:bg-inherit transition-all',
            )}
        >
            {!isParentHeader && (
                <div className="h-fit flex items-center gap-x-1 group-hover:bg-card rounded-full">
                    <SortingButton header={header} />
                    <ColumnMenu header={header} />
                </div>
            )}
        </span>,
    ]
}

function Cell({ header }: Pick<HeaderProp, 'header'>) {
    const ogDef = (header.column.columnDef as ColumnDefPro)._ogDef
    if (ogDef.renderHeader)
        return ogDef.renderHeader({
            field: ogDef.field,
            title: header.column.columnDef.header?.toString() || '',
        })
    return flexRender(header.column.columnDef.header, header.getContext())
}
